import { create } from "zustand";

/**
 * App-wide trigger for the **plan-limit dialog** — the upsell shown when a user
 * hits a NUMERIC plan quota (custom templates, broadcasts this month, published
 * policies). Distinct from the feature-lock dialog (which gates whole features by
 * tier); this one fires when a feature the user *has* runs out of allowance.
 *
 * The backend is the source of truth: quota 403s carry `code: "plan_limit"` and
 * a human-readable message. `maybeUpsellPlanLimit` detects that shape in the API
 * helpers and opens this dialog with the server's message, so every quota'd
 * endpoint upsells consistently without each call site re-deriving the copy.
 */

type PlanLimitState = {
  open: boolean;
  /** The server's explanation of which limit was hit, shown in the dialog. */
  message: string | null;
  openDialog: (message: string) => void;
  close: () => void;
};

const usePlanLimitStore = create<PlanLimitState>((set) => ({
  open: false,
  message: null,
  openDialog: (message) => set({ open: true, message }),
  close: () => set({ open: false }),
}));

/** Subscribe the dialog component to the store. */
export function usePlanLimitDialog() {
  return usePlanLimitStore();
}

/** Imperatively open the plan-limit dialog (from a catch block or the helper). */
export function openPlanLimit(message: string): void {
  usePlanLimitStore.getState().openDialog(message);
}

/**
 * Thrown by the API helpers after a plan-limit 403 has already been surfaced as
 * the upgrade dialog. Call sites can detect it (see {@link isPlanLimitError}) to
 * skip their own error toast — the dialog already tells the user what happened.
 */
export class PlanLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PlanLimitError";
  }
}

/** Whether an error is a already-handled plan-limit rejection. */
export function isPlanLimitError(err: unknown): boolean {
  return (
    err instanceof PlanLimitError ||
    (typeof err === "object" &&
      err !== null &&
      (err as { name?: string }).name === "PlanLimitError")
  );
}

/** The API's code for a numeric plan limit. `plan_limit` is what this file
 *  used to look for; the API has always sent `plan_limit_reached`. */
export function isPlanLimitCode(code: unknown): boolean {
  return code === "plan_limit_reached" || code === "plan_limit";
}

/**
 * If a failed response is a plan-limit 403 (`code: "plan_limit"`), open the
 * upgrade dialog with its message and return true. API helpers call this before
 * throwing so the upsell shows regardless of which endpoint hit the cap.
 */
export function maybeUpsellPlanLimit(status: number, data: unknown): boolean {
  if (status !== 403 || typeof data !== "object" || data === null) return false;
  const record = data as { code?: unknown; error?: unknown };
  if (!isPlanLimitCode(record.code)) return false;
  const message =
    typeof record.error === "string"
      ? record.error
      : "You've reached your plan's limit. Upgrade to add more.";
  openPlanLimit(message);
  return true;
}
